import {
  Box,
  Button,
  CardContent,
  CardHeader,
  Chip,
  Divider,
  Stack,
  Typography,
} from '@mui/material';
import { capitalizeWords } from '../../utils/helper';
import { Icon } from '@iconify-icon/react';

interface AcList {
  services: string[];
  acList: Record<string, number | string>;
}

interface CopperPiping {
  comments: string;
  quantity: string;
  place: string;
}

interface Amc {
  comments: string;
  quantity: string;
  place: string;
}

interface OtherService {
  service: string;
  problem: string;
  comments: string;
  acType?: string;
}

const PRIMARY_KEYS = [
  'SERVICE',
  'REPAIR',
  'INSTALLATION',
  'COMPRESSOR',
  'GAS_CHARGING',
];

// Group raw serviceDetails lines by service → appliance → components / Other,
// mirroring the booking-create review so the detail view reads the same way.
const groupPrimaryServices = (serviceDetails: any[]) => {
  const byService: Record<
    string,
    {
      label: string;
      appliances: Record<
        string,
        {
          items: { name: string; qty: number }[];
          others: { acType?: string; problems?: string; detail?: string }[];
        }
      >;
    }
  > = {};
  for (const d of serviceDetails || []) {
    const key = d?.service_data?.key;
    if (!key || !PRIMARY_KEYS.includes(key)) continue;
    if (!byService[key])
      byService[key] = {
        label: capitalizeWords(String(key).replace('_', ' ')),
        appliances: {},
      };
    const appliance = d.serviceType || 'General';
    if (!byService[key].appliances[appliance])
      byService[key].appliances[appliance] = { items: [], others: [] };
    if (d.acType === 'Other') {
      byService[key].appliances[appliance].others.push({
        acType: d.place,
        problems: d.otherService,
        detail: d.comment,
      });
    } else if (d.acType) {
      byService[key].appliances[appliance].items.push({
        name: capitalizeWords(d.acType),
        qty: Number(d.quantity) || 0,
      });
    }
  }
  return byService;
};

const GroupedPrimaryServices = ({
  serviceDetails,
}: {
  serviceDetails: any[];
}) => {
  const entries = Object.entries(groupPrimaryServices(serviceDetails));
  if (!entries.length) return null;
  return (
    <>
      {entries.map(([key, svc]) => {
        const total = Object.values(svc.appliances).reduce(
          (n, a) => n + a.items.reduce((m, it) => m + it.qty, 0),
          0,
        );
        return (
          <Box
            key={key}
            sx={{
              backgroundColor: '#1c1c1c',
              border: '1px solid rgba(255,255,255,0.07)',
              borderRadius: '12px',
              p: 1.75,
              my: 1.5,
            }}
          >
            <Box
              sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.25 }}
            >
              <Typography sx={{ color: '#0BE600', fontWeight: 700 }}>
                {svc.label}
              </Typography>
              <Chip
                size="small"
                label={`${total} unit${total !== 1 ? 's' : ''}`}
                sx={{
                  backgroundColor: 'rgba(168,85,247,0.18)',
                  color: '#C98BFF',
                  fontWeight: 600,
                  height: 22,
                }}
              />
            </Box>
            <Stack spacing={1}>
              {Object.entries(svc.appliances).map(([appliance, data]) => (
                <Box
                  key={appliance}
                  sx={{
                    display: 'flex',
                    gap: 1.5,
                    flexWrap: 'wrap',
                    py: 0.5,
                    borderTop: '1px solid rgba(255,255,255,0.05)',
                  }}
                >
                  <Typography
                    sx={{
                      color: '#A855F7',
                      fontWeight: 700,
                      fontSize: '0.85rem',
                      minWidth: 96,
                    }}
                  >
                    {appliance}
                  </Typography>
                  <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                    {data.items.map((it, i) => (
                      <Chip
                        key={`i${i}`}
                        size="small"
                        label={
                          <span>
                            {it.name}
                            <span
                              style={{ color: '#C98BFF', fontWeight: 700 }}
                            >{`  ×${it.qty}`}</span>
                          </span>
                        }
                        sx={{
                          backgroundColor: '#262626',
                          color: '#e6e6e6',
                          borderRadius: '8px',
                          border: '1px solid rgba(255,255,255,0.08)',
                        }}
                      />
                    ))}
                    {data.others.map((o, i) => {
                      const parts = [o.acType, o.problems, o.detail].filter(
                        Boolean,
                      );
                      return (
                        <Chip
                          key={`o${i}`}
                          size="small"
                          label={`Other${parts.length ? `: ${parts.join(' · ')}` : ''}`?.replaceAll("_"," ")}
                          sx={{
                            backgroundColor: 'rgba(224,168,46,0.16)',
                            color: '#E0A82E',
                            borderRadius: '8px',
                            border: '1px solid rgba(224,168,46,0.4)',
                          }}
                        />
                      );
                    })}
                  </Box>
                </Box>
              ))}
            </Stack>
          </Box>
        );
      })}
    </>
  );
};
export const ViewBookedServices = ({
  basicService,
  commercialAcService,
  copperpipingService,
  amcservice,
  otherService,
  serviceDetails,
  readonly = true, // <<<< ADD THIS
  onEditService, // (category: string, categoryKey?: string) => void;
}: {
  basicService: Record<string, Record<string, number>> | null;
  commercialAcService: AcList | null;
  copperpipingService: CopperPiping | null;
  amcservice: Amc | null;
  otherService: OtherService | null;
  // raw booking.serviceDetails — when passed, primary services render grouped
  // by appliance (with the "Other" intake details) instead of the flat list.
  serviceDetails?: any[] | null;
  readonly?: boolean;
  onEditService?: (category: string, categoryKey?: string) => void;
}) => {
  console.log('commercialAcService', commercialAcService);
  console.log('XXXXSERVICESXXXX', {
    basicService,
    commercialAcService,
    copperpipingService,
    amcservice,
    otherService,
    serviceDetails,
  });
  return (
    <Box
      sx={{ backgroundColor: 'transparent', color: '#fff', borderRadius: 2 }}
    >
      <CardHeader
        title={<Typography variant="h6">Service Detail</Typography>}
      />
      <Divider sx={{ borderColor: '#565656' }} />
      <CardContent>
        {/* BASIC SERVICES — grouped by appliance when raw serviceDetails given */}
        {serviceDetails ? (
          <GroupedPrimaryServices serviceDetails={serviceDetails} />
        ) : (
          basicService &&
          Object.entries(basicService).map(([category, services]) => (
            <ServiceRow
              key={category}
              label={capitalizeWords(category.replace('_', ' '))}
              items={Object.entries(services).map(([type, quantity]) => ({
                label: `${capitalizeWords(type)}`,
                value: quantity,
              }))}
              readonly={readonly}
              onEdit={
                onEditService
                  ? () => onEditService('basic', category)
                  : undefined
              }
              showDivider={false}
            />
          ))
        )}
        {/* COMMERCIAL AC */}
        {commercialAcService && (
          <ServiceRow
            label="Commercial AC"
            items={Object.entries(commercialAcService.acList).map(
              ([type, qty]) => ({
                label: `${capitalizeWords(type)} `,
                value: qty,
              }),
            )}
            chips={commercialAcService.services}
            readonly={readonly}
            onEdit={
              onEditService ? () => onEditService('commercial') : undefined
            }
            showDivider={false}
          />
        )}
        {/* COPPER PIPING */}
        {copperpipingService && (
          <ServiceRow
            label="Copper Piping"
            items={[
              { label: capitalizeWords(copperpipingService.place) },
              {
                label: 'Quantity',
                value:
                  copperpipingService.quantity &&
                  copperpipingService.quantity.trim().length > 0
                    ? copperpipingService.quantity
                    : 'N/A',
              },
            ]}
            comment={copperpipingService.comments}
            readonly={readonly}
            onEdit={
              onEditService ? () => onEditService('copperpiping') : undefined
            }
            showDivider={false}
          />
        )}
        {/* AMC */}
        {amcservice && (
          <ServiceRow
            label="AMC"
            items={[
              { label: capitalizeWords(amcservice.place) },
              { label: 'Quantity', value: amcservice.quantity },
            ]}
            comment={amcservice.comments}
            readonly={readonly}
            onEdit={onEditService ? () => onEditService('amc') : undefined}
            showDivider={false}
          />
        )}
        {/* OTHER SERVICE */}
        {otherService && (
          <ServiceRow
            label="Other"
            items={[
              { label: 'AC Type', value: otherService.acType || 'N/A' },
              { label: 'Problem', value: otherService.problem || 'N/A' },
            ]}
            comment={otherService.comments}
            readonly={readonly}
            onEdit={onEditService ? () => onEditService('other') : undefined}
            showDivider={false}
          />
        )}
      </CardContent>
    </Box>
  );
};

interface ServiceRowProps {
  label: string;
  items: { label: string; value?: string | number }[];
  chips?: string[];
  comment?: string;
  showDivider?: boolean;
  readonly?: boolean;
  onEdit?: () => void;
  onClose?: () => void;
}

export const ServiceRow: React.FC<ServiceRowProps> = ({
  label,
  items,
  chips,
  comment,
  showDivider = false,
  readonly = true,
  onEdit,
  onClose,
}) => {
  console.log('READONLY', readonly);
  return (
    <Box
      sx={{
        backgroundColor: readonly ? '#222' : '#1c1c1c',
        borderRadius: '8px',
        display: 'flex',
        flexDirection: 'column',
        my: 2,
        px: 1.5,
        py: 1.5,
        position: 'relative',
        width: '100%',
      }}
    >
      {/* Top Row with Edit */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          flexWrap: 'wrap',
          justifyContent: 'space-between',
        }}
      >
        <Typography
          sx={{
            display: 'flex',
            alignItems: 'center',
            flexWrap: 'wrap',
          }}
        >
          <Typography
            variant="body1"
            sx={{
              color: '#0BE600',
              minWidth: '120px',
              fontWeight: 500,
              mr: 2,
            }}
          >
            {capitalizeWords(label)}
          </Typography>
          {items.map((item, idx) => (
            <Typography
              key={idx}
              sx={{
                // backgroundColor: '#222',
                borderRadius: 1,
                fontSize: '0.875rem',
                color: '#FFFFFF',
                minWidth: '80px',
                fontWeight: 500,
                mr: 2,
                py: 0.5,
              }}
            >
              {capitalizeWords(item.label)}
              {item.value !== undefined ? ` - ${item.value}` : ''}
            </Typography>
          ))}
        </Typography>

        {!readonly && onEdit && (
          <div className="flex items-center gap-2">
            <Icon
              icon="ic:round-close"
              width="24"
              height="24"
              style={{ color: '#7B1FA2', cursor: 'pointer' }}
              onClick={onClose}
            />{' '}
            <Button
              variant="contained"
              onClick={onEdit}
              size="small"
              sx={{
                ml: 'auto',
                borderRadius: '8px',
                fontWeight: 500,
                px: 3,
                height: 32,
                backgroundColor: '#7B1FA2',
                '&:hover': { backgroundColor: '#5E167A' },
              }}
            >
              Edit
            </Button>
          </div>
        )}
      </Box>
      {/* Service chips row (optional) */}
      {(chips ?? []).length > 0 && (
        <Stack direction="row" spacing={1} flexWrap="wrap" mb={1}>
          {(chips ?? []).map((ser, idx) => (
            <Chip
              key={idx}
              label={ser}
              sx={{
                backgroundColor: '#333',
                color: 'white',
                fontSize: '0.8rem',
                borderRadius: '6px',
                my: 1,
              }}
            />
          ))}
        </Stack>
      )}
      {/* Divider */}
      {showDivider && (
        <Box sx={{ borderTop: '1px solid #333', mt: 1, mb: 1 }} />
      )}
      {/* Comment row (optional) */}
      {comment !== undefined && (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            backgroundColor: '#333',
            borderRadius: '0 0 8px 8px',
            px: 1,
            py: 1,
          }}
        >
          <Typography
            variant="body2"
            sx={{
              color: 'lightgray',
              fontWeight: 'bold',
              whiteSpace: 'nowrap',
            }}
          >
            Comment:
          </Typography>
          <Typography
            variant="body2"
            sx={{
              color: 'white',
              flex: 1,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
            }}
          >
            {comment || 'No comments provided'}
          </Typography>
        </Box>
      )}
    </Box>
  );
};
